(() => {
  'use strict';
  let controller = null;
  let revision = 0;
  const theme = window.CTTheme;
  if (!theme) return;
  const indexes = new WeakMap();
  const renderedAt = new WeakMap();
  const sectionCache = new Map();
  const fieldsFor = root => [...root.querySelectorAll('.ct-option')];
  const updateLegends = root => fieldsFor(root).forEach(field => {
    const selected = field.querySelector('[data-ct-option]:checked');
    const legend = field.querySelector('legend strong');
    if (selected && legend) legend.textContent = selected.value;
  });
  const variantIndex = root => {
    if (performance.now() - (renderedAt.get(root) ?? -Infinity) > 300000) return null;
    if (indexes.has(root)) return indexes.get(root);
    let result = null;
    try {
      const source = root.querySelector('[data-neb-variant-index]');
      const data = source ? JSON.parse(source.textContent) : null;
      const count = fieldsFor(root).length;
      if (data?.complete && String(data.product_id) === root.dataset.productId && Array.isArray(data.variants) && data.variants.length === data.count && data.count <= 50 && data.variants.every(row => Number.isSafeInteger(row.id) && row.id > 0 && Number.isFinite(row.price) && row.price >= 0 && typeof row.available === 'boolean' && Array.isArray(row.options) && row.options.length === count)) {
        result = { ...data, byOptions: new Map(data.variants.map(row => [JSON.stringify(row.options), row])) };
      }
    } catch (_) {}
    indexes.set(root, result);
    return result;
  };
  const setText = (root, selector, value) => {
    const target = root.querySelector(selector);
    if (target) target.textContent = value;
  };
  const applyLocalVariant = (root, changed) => {
    if (root.dataset.ctSubmitting === 'true' || root.querySelector('[name=selling_plan]')?.value) return false;
    const own = new URL(root.dataset.productUrl, location.origin);
    const requested = new URL(changed.dataset.productUrl || root.dataset.productUrl, location.origin);
    if (own.origin !== requested.origin || own.pathname !== requested.pathname) return false;
    const data = variantIndex(root);
    if (!data) return false;
    const fields = fieldsFor(root);
    const selected = fields.map(field => field.querySelector('[data-ct-option]:checked')?.value);
    const row = data.byOptions.get(JSON.stringify(selected));
    const id = root.querySelector('[data-ct-product-form] [name=id]');
    const slides = [...root.querySelectorAll('[data-ct-gallery] [data-media-id]')];
    if (!row || !id || (row.media_id && !slides.some(slide => slide.dataset.mediaId === String(row.media_id)))) return false;
    controller?.abort();
    controller = null;
    revision++;
    id.value = String(row.id);
    id.disabled = !row.available;
    root.dataset.variantId = String(row.id);
    root.dataset.nebV12UnitPrice = String(row.price);
    root.removeAttribute('aria-busy');
    updateLegends(root);
    fields.forEach((field, index) => field.querySelectorAll('[data-ct-option]').forEach(input => {
      const available = data.variants.some(candidate => candidate.available && candidate.options[index] === input.value && candidate.options.slice(0, index).every((value, prefix) => value === selected[prefix]));
      const label = [...input.labels].find(node => node.classList.contains('ct-option__label'));
      if (!label) return;
      label.classList.toggle('ct-option__label--unavailable', !available);
      const hint = label.querySelector('[data-neb-option-unavailable]');
      if (available) hint?.remove();
      else if (!hint) {
        const text = document.createElement('span');
        text.className = 'ct-sr-only';
        text.dataset.nebOptionUnavailable = '';
        text.textContent = ' — ' + data.labels.unavailable;
        label.append(text);
      }
    }));
    const quantity = root.querySelector('.ct-quantity [name=quantity]');
    if (quantity) {
      const minimum = Math.max(1, Number(row.quantity_rule?.min) || 1);
      const increment = Math.max(1, Number(row.quantity_rule?.increment) || 1);
      const maximum = Number(row.quantity_rule?.max) > 0 ? Number(row.quantity_rule.max) : Infinity;
      const current = Number(quantity.value) || minimum;
      quantity.min = String(minimum);
      quantity.step = String(increment);
      if (Number.isFinite(maximum)) quantity.max = String(maximum);
      else quantity.removeAttribute('max');
      quantity.value = String(Math.min(maximum, Math.max(minimum, Math.ceil(current / increment) * increment)));
    }
    setText(root, '[data-neb-price-current]', row.price_text);
    setText(root, '[data-neb-price-compare]', row.compare_text);
    setText(root, '[data-neb-price-saving]', row.saving_text);
    root.querySelector('[data-neb-price-amount]')?.classList.toggle('neb-price__amount--long', !!row.long_price);
    root.querySelectorAll('[data-neb-price-compare],[data-neb-price-previous-label],[data-neb-price-saving]').forEach(node => node.hidden = !row.on_sale);
    root.querySelectorAll('[data-neb-stock-in]').forEach(node => node.hidden = !row.available);
    root.querySelectorAll('[data-neb-stock-out]').forEach(node => node.hidden = row.available);
    root.querySelectorAll('[name=add],[data-neb-buy-now]').forEach(button => button.disabled = !row.available);
    setText(root, '.neb-v12-atc__label', row.available ? data.labels.add : data.labels.sold_out);
    root.querySelectorAll('.neb-v12-atc__divider,.ct-atc [data-neb-v12-atc-total]').forEach(node => node.hidden = !row.available);
    root.querySelectorAll('[data-neb-offer-cycle]').forEach(node => node.hidden = !row.available || !row.on_sale);
    if (row.media_id) {
      slides.forEach(slide => {
        slide.hidden = slide.dataset.mediaId !== String(row.media_id);
        if (slide.hidden) slide.querySelectorAll('video').forEach(video => video.pause());
        else slide.querySelectorAll('img').forEach(image => { image.loading = 'eager'; image.fetchPriority = 'high'; });
      });
      root.querySelectorAll('[data-ct-media]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ctMedia === String(row.media_id))));
    }
    setText(root, '[data-ct-product-status]', '');
    const url = new URL(location.href);
    if (url.pathname !== own.pathname) url.pathname = own.pathname;
    url.searchParams.delete('section_id');
    url.searchParams.delete('option_values');
    url.searchParams.set('variant', String(row.id));
    history.replaceState({}, '', url);
    document.dispatchEvent(new CustomEvent('ct:variant-changed', { detail: { variantId: String(row.id) } }));
    return true;
  };
  const fetchSection = async (url, signal) => {
    const key = url.toString();
    const cached = sectionCache.get(key);
    if (cached && performance.now() - cached.time < 30000) return cached.html;
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(theme.strings.retry);
    const html = await response.text();
    sectionCache.delete(key);
    sectionCache.set(key, { html, time: performance.now() });
    if (sectionCache.size > 6) sectionCache.delete(sectionCache.keys().next().value);
    return html;
  };
  const enableOptions = root => {
    root.querySelectorAll('[data-ct-option]').forEach(input => { input.disabled = false; });
    const products = root.matches?.('[data-ct-product]') ? [root] : [...root.querySelectorAll('[data-ct-product]')];
    products.forEach(product => { if (!renderedAt.has(product)) renderedAt.set(product, performance.now()); });
  };
  enableOptions(document);
  document.addEventListener('shopify:section:load', event => { enableOptions(event.target); });
  const showError = (root, message) => {
    const status = root.querySelector('[data-ct-product-status]');
    if (status) status.textContent = message;
  };
  document.addEventListener('input', event => {
    if (event.target.matches('[data-ct-personal-field]')) event.target.closest('.ct-personalize').querySelectorAll('input[type=text]').forEach(input => input.setCustomValidity(''));
  });
  document.addEventListener('change', async event => {
    if (!event.target.matches('[data-ct-option]')) return;
    const root = event.target.closest('[data-ct-product]');
    if (!root || root.dataset.ctSubmitting === 'true') return;
    if (applyLocalVariant(root, event.target)) return;
    updateLegends(root);
    const lastInput = event.target.id;
    const optionIds = [...root.querySelectorAll('[data-ct-option]:checked')].map(input => input.dataset.optionValueId);
    const productUrl = event.target.dataset.productUrl || root.dataset.productUrl;
    const url = new URL(productUrl, location.origin);
    url.searchParams.set('section_id', root.dataset.sectionId);
    url.searchParams.set('option_values', optionIds.join(','));
    url.searchParams.delete('variant');
    const quantity = root.querySelector('[name=quantity]')?.value;
    const personalize = root.querySelector('[data-ct-personalize-choice][value="yes"]')?.checked;
    const properties = [...root.querySelectorAll('[data-ct-personal-field]')].map(input => [input.name, input.value]);
    const activeTab = root.querySelector('[data-ct-tab][aria-selected=true]')?.dataset.ctTab;
    controller?.abort();
    controller = new AbortController();
    const requestController = controller;
    const timeout = setTimeout(() => requestController.abort(new Error(theme.strings.retry)), 15000);
    const token = ++revision;
    root.setAttribute('aria-busy', 'true');
    const atc = root.querySelector('[name=add]');
    root.querySelectorAll('[name=add],[data-neb-buy-now]').forEach(button => button.disabled = true);
    const pendingId = root.querySelector('[data-ct-product-form] [name=id]');
    if (pendingId) pendingId.disabled = true;
    showError(root, theme.strings.selection_updating || '');
    try {
      const html = new DOMParser().parseFromString(await fetchSection(url, controller.signal), 'text/html');
      const replacement = html.querySelector('[data-ct-product]');
      if (!replacement || token !== revision) throw new Error(theme.strings.retry);
      enableOptions(replacement);
      const quantityInput = replacement.querySelector('[name=quantity]');
      if (quantityInput && quantity) quantityInput.value = String(Math.max(Number(quantityInput.min) || 1, Math.min(quantityInput.max ? Number(quantityInput.max) : Infinity, Number(quantity))));
      const yesChoice = replacement.querySelector('[data-ct-personalize-choice][value="yes"]');
      if (yesChoice && personalize) {
        yesChoice.checked = true;
        replacement.querySelector('[data-ct-personalize-choice][value="no"]').checked = false;
        const widget = replacement.querySelector('[data-ct-personalize-root]');
        widget?.classList.add('neb-personalize--active');
        replacement.querySelector('.ct-personalize__fields').hidden = false;
        replacement.querySelectorAll('[data-ct-personal-field]').forEach(input => {
          input.disabled = false;
          input.required = true;
          input.value = properties.find(pair => pair[0] === input.name)?.[1] || '';
        });
      }
      root.replaceWith(replacement);
      theme.init(replacement);
      if (activeTab) replacement.querySelector('[data-ct-tab="' + activeTab + '"]')?.click();
      document.getElementById(lastInput)?.focus({ preventScroll: true });
      url.searchParams.delete('section_id');
      history.replaceState({}, '', url);
      if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
      document.dispatchEvent(new CustomEvent('ct:variant-changed', { detail: { variantId: replacement.dataset.variantId } }));
    } catch (error) {
      if (error.name === 'AbortError' || token !== revision) return;
      sectionCache.delete(url.toString());
      root.removeAttribute('aria-busy');
      showError(root, theme.strings.variant_retry);
      if (atc) atc.disabled = true;
      const status = root.querySelector('[data-ct-product-status]');
      if (status) {
        const link = document.createElement('a');
        url.searchParams.delete('section_id');
        link.href = url.toString();
        link.textContent = theme.strings.load_selection;
        link.className = 'ct-text-link';
        status.append(' ', link);
      }
    } finally { clearTimeout(timeout); }
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-ct-product-form]');
    if (!form) return;
    event.preventDefault();
    const root = form.closest('[data-ct-product]');
    if (!root || root.getAttribute('aria-busy') === 'true') return;
    const yes = form.querySelector('[data-ct-personalize-choice][value="yes"]')?.checked;
    if (yes) {
      const fields = [...form.querySelectorAll('[data-ct-personal-field]')];
      for (const input of fields) {
        input.value = input.value.trim();
        input.setCustomValidity('');
        if (!input.value) {
          input.setCustomValidity('Enter a value or type Blank.');
          input.reportValidity();
          return;
        }
        if (!input.checkValidity()) { input.reportValidity(); return; }
      }
    }
    const buyNow = event.submitter?.matches('[data-neb-buy-now]');
    const buttons = [...form.querySelectorAll('[name=add],[data-neb-buy-now]')];
    const button = form.querySelector('[name=add]');
    const variant = form.querySelector('[name=id]');
    if (!variant || variant.disabled || !variant.value) return;
    buttons.forEach(button => { button.disabled = true; });
    root.setAttribute('aria-busy', 'true');
    root.dataset.ctSubmitting = 'true';
    form.querySelectorAll('[data-ct-option]').forEach(input => input.disabled = true);
    showError(root, theme.strings.adding);
    try {
      const cartData = new FormData(form);
      cartData.set('sections','neb-cart-drawer');
      cartData.set('sections_url',theme.routes.cart);
      const response = await fetch(theme.routes.add + '.js', { method: 'POST', headers: { Accept: 'application/json' }, body: cartData, signal: AbortSignal.timeout(15000) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || theme.strings.retry);
      if (buyNow) { location.assign(root.dataset.checkoutUrl);return; }
      if (window.NebCart) {
        try { await window.NebCart.added(result,button);showError(root,theme.strings.added||''); }
        catch (_) { location.assign(theme.routes.cart);return; }
        buttons.forEach(control=>{control.disabled=false;});root.removeAttribute('aria-busy');
      } else location.assign(theme.routes.cart);
    } catch (error) {
      showError(root, error.name === 'TimeoutError' ? theme.strings.retry : error.message || theme.strings.retry);
      buttons.forEach(button => { button.disabled = false; });
      root.removeAttribute('aria-busy');
    } finally {
      delete root.dataset.ctSubmitting;
      form.querySelectorAll('[data-ct-option]').forEach(input => input.disabled = false);
    }
  });
  document.addEventListener('click', event => {
    const thumb = event.target.closest('[data-ct-media]');
    if (thumb) {
      const gallery = thumb.closest('[data-ct-gallery]');
      gallery.querySelectorAll('[data-media-id]').forEach(slide => {
        slide.hidden = slide.dataset.mediaId !== thumb.dataset.ctMedia;
        if (slide.hidden) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      gallery.querySelectorAll('[data-ct-media]').forEach(button => button.setAttribute('aria-pressed', String(button === thumb)));
    }
    const zoom = event.target.closest('[data-ct-zoom]');
    if (zoom) {
      const image = zoom.closest('.ct-gallery__slide')?.querySelector('img');
      if (!image) return;
      const dialog = document.createElement('dialog');
      dialog.className = 'ct-zoom-dialog';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'ct-icon-btn';
      close.setAttribute('aria-label', theme.strings.close);
      close.textContent = '×';
      const enlarged = image.cloneNode();
      enlarged.removeAttribute('fetchpriority');
      enlarged.removeAttribute('srcset');
      enlarged.removeAttribute('sizes');
      enlarged.loading = 'eager';
      dialog.append(close, enlarged);
      document.body.append(dialog);
      dialog.setAttribute('aria-label', image.alt || theme.strings.zoom);
      dialog.showModal();
      close.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); });
      dialog.addEventListener('close', () => { dialog.remove(); zoom.focus({ preventScroll: true }); }, { once: true });
    }
  });
})();
